import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Link2, Loader2, RefreshCw } from 'lucide-react';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';
import PublicFormSubmissions from './PublicFormSubmissions';

const STATUS_LABELS = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  exhausted: 'Exhausted',
};

const formatTimestamp = (value, fallback = '—') => {
  if (!value) return fallback;
  // MySQL's link timestamps are UTC; do not interpret them as browser-local time.
  const text = String(value).replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  const date = new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleString();
};

const PublicFormLinksScope = ({ form, endpoint, onBack, onCreateLink, onRevoked }) => {
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [confirmId, setConfirmId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [submissionLink, setSubmissionLink] = useState(null);
  const aliveRef = useRef(false);
  const mutationRef = useRef(false);
  const reviewRef = useRef(null);
  const reviewTriggerRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setConfirmId(null);
    setSubmissionLink(null);
    setLinks([]);
    publicFormsApi.links(form.id, { endpoint, page, perPage: 25, signal: controller.signal })
      .then((payload) => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(payload.links) || !payload.pagination) {
          throw new Error('CaseDB could not load recipient link history. Please refresh or try again later.');
        }
        setLinks(payload.links);
        setPagination(payload.pagination);
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) setError(publicFormFeatureError(requestError));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [endpoint, form.id, page, revision]);

  useEffect(() => {
    if (submissionLink === null) return;
    reviewRef.current?.scrollIntoView?.({ block: 'start' });
    reviewRef.current?.focus({ preventScroll: true });
  }, [submissionLink]);

  const revoke = async (link) => {
    if (mutationRef.current) return;
    if (confirmId !== link.id) {
      setConfirmId(link.id);
      setNotice('');
      return;
    }
    mutationRef.current = true;
    setRevokingId(link.id);
    setError('');
    try {
      await publicFormsApi.revokeLink(link.id, { endpoint });
      onRevoked?.(link.id);
      if (!aliveRef.current) return;
      setNotice(`Link #${link.id} revoked. Existing submissions are preserved.`);
      setConfirmId(null);
      setRevision((value) => value + 1);
    } catch (requestError) {
      if (aliveRef.current) setError(publicFormFeatureError(requestError));
    } finally {
      mutationRef.current = false;
      if (aliveRef.current) setRevokingId(null);
    }
  };

  const busy = loading || revokingId !== null;
  const total = Number(pagination?.total || 0);
  const totalPages = Number(pagination?.total_pages || 0);

  return (
    <section className="public-form-builder-panel public-form-links" aria-labelledby="public-form-links-title">
      <div className="public-form-panel-heading public-form-links-heading">
        <div>
          <h3 id="public-form-links-title">Recipient links</h3>
          <p className="subtext">Each link has its own destination and submission history. Link status does not confirm delivery or that it was opened.</p>
        </div>
        <div className="public-form-links-toolbar">
          <button type="button" className="action quaternary" onClick={onBack} disabled={revokingId !== null}><ArrowLeft size={15} /> Back to editing</button>
          <button type="button" className="action quaternary" onClick={() => setRevision((value) => value + 1)} disabled={busy}><RefreshCw size={15} /> Refresh links</button>
          <button type="button" className="action" onClick={onCreateLink} disabled={revokingId !== null || form.status === 'revoked'}><Link2 size={15} /> Create a link</button>
        </div>
      </div>
      <p className="subtext public-form-links-security">For security, saved links cannot be copied again. Copy a new link when you create it; creating another does not revoke the previous one.</p>
      {error && <div className="public-form-error" role="alert">{error}</div>}
      {notice && <p className="subtext" role="status">{notice}</p>}
      {confirmId !== null && <p className="subtext" role="status">Revoking link #{confirmId} prevents further use. Existing submissions will remain. Select Confirm revoke to continue.</p>}
      {loading && <div className="public-form-links-loading" role="status"><Loader2 size={16} className="spinner" /> Loading recipient links…</div>}
      {!loading && !error && links.length === 0 && <div className="public-form-empty-state compact">
        <strong>{page === 1 ? 'No recipient links yet' : 'No links on this page'}</strong>
        <p>{page === 1 ? 'Create a link for each case or lead you want to collect information from. New-record forms create their destination after submission.' : 'Return to the previous page to see earlier results.'}</p>
      </div>}
      {!loading && links.length > 0 && <div className="public-forms-table-wrap">
        <table className="public-forms-table public-form-links-table" aria-label="Recipient links">
          <thead><tr><th>Case / lead</th><th>Link</th><th>Status</th><th>Created / expires</th><th>Submissions</th><th>Latest response</th><th>Actions</th></tr></thead>
          <tbody>{links.map((link) => {
            const state = Object.prototype.hasOwnProperty.call(STATUS_LABELS, link.effective_status) ? link.effective_status : 'unknown';
            const count = Number(link.submission_count || 0);
            const canRevoke = link.status === 'active' && ['active', 'expired', 'exhausted'].includes(state);
            const confirming = confirmId === link.id;
            return <tr key={link.id}>
              <td>
                <span className="public-form-links-target">{link.target_name || 'Unavailable record'}</span>
                {link.target_accessible && link.target_id && <small>{link.target_type === 'case' ? 'Case' : 'Lead'} #{link.target_id}</small>}
              </td>
              <td>#{link.id}<small>Form version {link.schema_version}</small></td>
              <td><span className={`public-form-status public-form-status-${state === 'active' ? 'published' : state}`}>{STATUS_LABELS[state] || 'Unavailable'}</span></td>
              <td>{formatTimestamp(link.created_at)}<small>Expires {formatTimestamp(link.expires_at, '—')}</small></td>
              <td>{count}<small>{Number(link.use_count || 0)} of {link.max_uses == null ? 'unlimited' : link.max_uses} uses</small></td>
              <td>{formatTimestamp(link.last_submitted_at, 'No response yet')}</td>
              <td><div className="public-form-row-actions">
                <button type="button" className="action-tiny quaternary public-form-row-action public-form-row-action--view" disabled={count === 0 || revokingId !== null} onClick={(event) => { reviewTriggerRef.current = event.currentTarget; setSubmissionLink(link.id); }} aria-label={`View submissions for link ${link.id}`}>Submissions</button>
                {canRevoke && <button type="button" className={`action-tiny public-form-row-action public-form-row-action--revoke ${confirming ? 'destroy' : 'quinary'}`} disabled={revokingId !== null} onClick={() => revoke(link)} aria-label={`${confirming ? 'Confirm revoke' : 'Revoke'} link ${link.id}`}>
                  {revokingId === link.id ? 'Revoking…' : confirming ? 'Confirm revoke' : 'Revoke'}
                </button>}
                {confirming && <button type="button" className="action-tiny quaternary" disabled={revokingId !== null} onClick={() => setConfirmId(null)}>Cancel</button>}
              </div></td>
            </tr>;
          })}</tbody>
        </table>
      </div>}
      {!loading && pagination && <div className="pagination-controls public-form-links-pagination" aria-label="Recipient links pages">
        <span className="subtext">{total} link{total === 1 ? '' : 's'}{totalPages > 0 ? ` · Page ${page} of ${totalPages}` : ''}</span>
        <button type="button" className="action quaternary" disabled={page <= 1 || busy} onClick={() => { setSubmissionLink(null); setPage((value) => value - 1); }}>Previous</button>
        <button type="button" className="action quaternary" disabled={page >= totalPages || busy} onClick={() => { setSubmissionLink(null); setPage((value) => value + 1); }}>Next</button>
      </div>}
      {submissionLink !== null && <div ref={reviewRef} tabIndex={-1} className="public-form-link-review">
        <PublicFormSubmissions
          key={submissionLink}
          form={form}
          endpoint={endpoint}
          linkId={submissionLink}
          embedded
          onBack={() => {
            setSubmissionLink(null);
            reviewTriggerRef.current?.focus();
          }}
        />
      </div>}
    </section>
  );
};

// Scope changes discard previous destinations and decrypted submission state.
const PublicFormLinks = (props) => <PublicFormLinksScope key={`${props.form.id}:${props.endpoint || ''}`} {...props} />;

export default PublicFormLinks;
